import React from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { AddressTypes, DisplayText, ModalRow, theme } from '@sg/garnish';
import { Map } from '@sg/radar-fe';

import { getEnvVars } from '@order/utils';

import { DeliveryLocationCard } from '../DeliveryLocationCard';
import { RestaurantLocationCard } from '../RestaurantLocationCard';
import { ConfirmLocationLayoutFooter } from './ConfirmLocationLayoutFooter';

import type { ConfirmLocationLayoutProps } from './ConfirmLocationLayout.types';

export const ConfirmLocationLayoutSM = (props: ConfirmLocationLayoutProps) => {
  const {
    deliveryAddress,
    isLoading,
    onContinue,
    onMoreLocations,
    orderType,
    pins,
    region,
    restaurant,
  } = props;

  const { formatMessage } = useIntl();

  const isDelivery = orderType === AddressTypes.delivery;

  return (
    <View testID="confirm-location.container-sm" style={styles.container}>
      <View style={styles.mapContainer}>
        <Map publishableKey={RADAR_SDK_KEY} region={region} pins={pins} />
      </View>

      <View style={styles.contentContainer}>
        <ModalRow>
          <DisplayText size={4} style={styles.headline}>
            {formatMessage(messages.locationConfirmLocation, {
              channel: orderType,
            })}
          </DisplayText>

          {isDelivery && deliveryAddress ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard orderType={orderType} restaurant={restaurant} />
          )}
        </ModalRow>

        <ConfirmLocationLayoutFooter
          isLoading={isLoading}
          onMoreLocations={onMoreLocations}
          onContinue={onContinue}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 500,
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  mapContainer: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
    width: '50%',
  },
  contentContainer: {
    flex: 1,
    width: '50%',
    justifyContent: 'space-around',
    paddingTop: 48,
  },
  headline: {
    marginBottom: theme.spacing['2'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly locationConfirmLocation: { readonly channel: MessageValue };
}>({
  locationConfirmLocation: {
    defaultMessage: 'Confirm {channel} location',
    description: 'Location | Confirm Location',
  },
});
